import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import {
  ConvertedItemLocator,
  QaTask,
  SpringPage,
  Suggestion,
  SuggestionAuthor,
  SuggestionComment,
  SuggestionFile,
  SuggestionFilters,
  SuggestionRequest,
  SuggestionStatus
} from '../models/suggestion.model';

@Injectable({ providedIn: 'root' })
export class GdpSuggestionService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.baseApiUrl}/gdp/suggestions`;

  getMySuggestions(page = 0, size = 20, filters: SuggestionFilters = {}): Observable<SpringPage<Suggestion>> {
    return this.http.get<SpringPage<Suggestion>>(this.apiUrl, {
      params: this.listParams(page, size, filters)
    });
  }

  /** Solo ADMIN */
  getAllSuggestions(page = 0, size = 20, filters: SuggestionFilters = {}): Observable<SpringPage<Suggestion>> {
    return this.http.get<SpringPage<Suggestion>>(`${this.apiUrl}/all`, {
      params: this.listParams(page, size, filters)
    });
  }

  /** Solo ADMIN: autores con alguna sugerencia (selector del filtro por autor) */
  getAuthors(): Observable<SuggestionAuthor[]> {
    return this.http.get<SuggestionAuthor[]>(`${this.apiUrl}/authors`);
  }

  /** Solo ADMIN: marca la sugerencia como revisada (quita el label NUEVA) */
  markReviewed(suggestionId: number): Observable<Suggestion> {
    return this.http.put<Suggestion>(`${this.apiUrl}/${suggestionId}/review`, {});
  }

  private listParams(page: number, size: number, filters: SuggestionFilters): Record<string, string | number> {
    const params: Record<string, string | number> = { page, size };
    if (filters.search?.trim()) {
      params['search'] = filters.search.trim();
    }
    if (filters.type) {
      params['type'] = filters.type;
    }
    if (filters.authorId != null) {
      params['authorId'] = filters.authorId;
    }
    return params;
  }

  createSuggestion(request: SuggestionRequest): Observable<Suggestion> {
    return this.http.post<Suggestion>(this.apiUrl, request);
  }

  /** Tareas de sprints abiertos donde soy QA (habilita el tipo QA Feedback). */
  getMyQaTasks(): Observable<QaTask[]> {
    return this.http.get<QaTask[]>(`${this.apiUrl}/qa-tasks/mine`);
  }

  /** QA feedback ligado a una tarea. */
  getTaskQaFeedback(taskId: number): Observable<Suggestion[]> {
    return this.http.get<Suggestion[]>(`${this.apiUrl}/tasks/${taskId}/qa-feedback`);
  }

  updateSuggestion(suggestionId: number, request: SuggestionRequest): Observable<Suggestion> {
    return this.http.put<Suggestion>(`${this.apiUrl}/${suggestionId}`, request);
  }

  deleteSuggestion(suggestionId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${suggestionId}`);
  }

  /** Envía un borrador (DRAFT → SENT): recién entonces se notifica a los admins. */
  sendDraft(suggestionId: number): Observable<Suggestion> {
    return this.http.post<Suggestion>(`${this.apiUrl}/${suggestionId}/send`, {});
  }

  /** Solo ADMIN: marca ACCEPTED/REJECTED, o SENT para deshacer (mientras no esté convertida). */
  decideSuggestion(suggestionId: number, status: SuggestionStatus): Observable<Suggestion> {
    return this.http.put<Suggestion>(`${this.apiUrl}/${suggestionId}/decision`, { status });
  }

  /** Ubicación actual del item convertido (se resuelve al hacer click). */
  getConvertedLocator(suggestionId: number): Observable<ConvertedItemLocator> {
    return this.http.get<ConvertedItemLocator>(`${this.apiUrl}/${suggestionId}/converted-locator`);
  }

  // ── Comentarios (autor ↔ admins GDP) ───────────────────────────────────────

  getComments(suggestionId: number): Observable<SuggestionComment[]> {
    return this.http.get<SuggestionComment[]>(`${this.apiUrl}/${suggestionId}/comments`);
  }

  addComment(suggestionId: number, text: string): Observable<SuggestionComment> {
    return this.http.post<SuggestionComment>(`${this.apiUrl}/${suggestionId}/comments`, { text });
  }

  deleteComment(commentId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/comments/${commentId}`);
  }

  /** Solo ADMIN: convierte la sugerencia en tarea de un backlog o de un sprint abierto */
  convertToTask(
    suggestionId: number,
    projectId: number,
    backlogId: number | null,
    sprintId: number | null = null
  ): Observable<Suggestion> {
    return this.http.post<Suggestion>(`${this.apiUrl}/${suggestionId}/convert`, { projectId, backlogId, sprintId });
  }

  // ── Archivos ───────────────────────────────────────────────────────────────

  getFiles(suggestionId: number): Observable<SuggestionFile[]> {
    return this.http.get<SuggestionFile[]>(`${this.apiUrl}/${suggestionId}/files`);
  }

  uploadFile(suggestionId: number, file: File): Observable<SuggestionFile> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<SuggestionFile>(`${this.apiUrl}/${suggestionId}/files`, formData);
  }

  downloadFile(suggestionId: number, fileId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/${suggestionId}/files/${fileId}/download`, { responseType: 'blob' });
  }

  deleteFile(suggestionId: number, fileId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${suggestionId}/files/${fileId}`);
  }
}
